/**
 * module.cart-bundle — cart-side behaviour for grouped bundle lines.
 *
 * Pure helpers (exported, unit-tested) compute the /cart/update.js payloads for
 * scaling the whole bundle by N and for cascade removal. The browser part uses
 * document-level event delegation so it survives ajax cart re-renders without
 * re-wiring. Mirrors the cascade idea in Klubb's klubb-cart-reconcile.js.
 */

// members: [{ key, unit, quantity }] — every member shares the same N.
export function currentMultiplier(members) {
  if (!members || members.length === 0) return 1;
  const m = members[0];
  const unit = m.unit || 1;
  return Math.max(1, Math.round((m.quantity || unit) / unit));
}

// Scale every member line to N × its per-set unit quantity (N clamped ≥ 1).
export function scaleUpdates(members, n) {
  const mult = Math.max(1, Math.round(n));
  const updates = {};
  (members || []).forEach((m) => { updates[m.key] = mult * (m.unit || 1); });
  return updates;
}

// Cascade removal: set every member line to 0 in one /cart/update.js.
export function removeUpdates(members) {
  const updates = {};
  (members || []).forEach((m) => { updates[m.key] = 0; });
  return updates;
}

/* ============================================================
 * Browser wiring (delegated; guarded for Vitest).
 * ============================================================ */

function readMembers(group) {
  return Array.from(group.querySelectorAll('[data-bundle-line]')).map((el) => ({
    key: el.getAttribute('data-key'),
    unit: Number(el.getAttribute('data-bundle-unit')) || 1,
    quantity: Number(el.getAttribute('data-bundle-qty')) || 0,
  }));
}

function postUpdates(updates) {
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates: updates }),
  })
    .then((r) => { if (!r.ok) throw new Error('cart update failed'); return r.json(); })
    .then(() => { document.dispatchEvent(new CustomEvent('cart:build')); })
    .catch((err) => { console.error('[module.cart-bundle]', err); });
}

function onClick(evt) {
  const inc = evt.target.closest('[data-bundle-qty-inc]');
  const dec = evt.target.closest('[data-bundle-qty-dec]');
  const rem = evt.target.closest('[data-bundle-remove]');
  if (!inc && !dec && !rem) return;
  const group = evt.target.closest('[data-bundle-group]');
  if (!group) return;
  evt.preventDefault();
  const members = readMembers(group);
  if (rem) { postUpdates(removeUpdates(members)); return; }
  const n = currentMultiplier(members) + (inc ? 1 : -1);
  postUpdates(scaleUpdates(members, n));
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (!window.__amoblerCartBundleWired) {
    window.__amoblerCartBundleWired = true;
    document.addEventListener('click', onClick);
  }
}
